import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, queryString } from "@anyseal/api-client";
import { Activity, ArrowUpRight, Boxes, CheckCircle2, Circle, CreditCard, KeyRound } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  DataTable,
  MetricCard,
  PageHeader,
  RequestId,
  Segmented,
  StatePanel,
  formatDate,
  formatNumber,
  toneForStatus,
  type Column,
} from "@anyseal/ui";
import { portalClient } from "../api";
import { useI18n } from "../i18n";
import { projectPath, useWorkspace } from "../workspace";
import { statusLabel } from "../status";
import { WorkspaceGuard } from "../WorkspaceGuard";

interface Usage {
  totals: { requests: number; input_tokens: number; output_tokens: number; spend_microunits: number; average_duration_ms: number };
  series: Array<{ bucket: string; requests: number }>;
}
interface Account { available_microunits: number; reserved_microunits: number; spent_microunits: number; debt_microunits: number; }
interface RecentRequest { request_id: string; model: string; protocol: string; status: string; input_tokens?: number; output_tokens?: number; started_at: string; }
interface RequestPage { items: RecentRequest[]; total: number; }
interface Credential { id: string; status: string; policy: { allowed_models?: string[]; allowed_cidrs?: string[] }; }

/** Merge a daily series into at most `target` buckets so long ranges stay
 *  readable (90 raw bars are unreadable at card width). */
function bucketSeries(series: Array<{ bucket: string; requests: number }>, target: number): Array<{ bucket: string; requests: number }> {
  if (series.length <= target) return series;
  const size = Math.ceil(series.length / target);
  const out: Array<{ bucket: string; requests: number }> = [];
  for (let i = 0; i < series.length; i += size) {
    const chunk = series.slice(i, i + size);
    const first = chunk[0];
    if (!first) continue;
    out.push({ bucket: first.bucket, requests: chunk.reduce((sum, item) => sum + item.requests, 0) });
  }
  return out;
}

export function OverviewPage() {
  return <WorkspaceGuard><OverviewPageInner /></WorkspaceGuard>;
}

function OverviewPageInner() {
  const { locale } = useI18n();
  const { organization, project } = useWorkspace();
  const [rangeDays, setRangeDays] = useState(30);
  const now = new Date();
  const from = new Date(now.getTime() - rangeDays * 86_400_000);
  const base = organization && project ? projectPath(organization.id, project.id) : "";
  const currency = organization?.default_currency ?? "CNY";
  const usage = useQuery({ queryKey: ["portal", "overview-usage", base, rangeDays], queryFn: () => portalClient.get<Usage>(`${base}/analytics/usage${queryString({ from: from.toISOString(), to: now.toISOString(), bucket: "day" })}`), enabled: Boolean(base) });
  const account = useQuery({ queryKey: ["portal", "billing-account", base], queryFn: () => portalClient.get<Account>(`${base}/billing/account`), enabled: Boolean(base) });
  const requests = useQuery({ queryKey: ["portal", "overview-requests", base], queryFn: () => portalClient.get<RequestPage>(`${base}/usage/requests?limit=5&offset=0`), enabled: Boolean(base) });
  // Real checklist state: a key counts as restricted when any credential
  // narrows its model allowlist or source networks.
  const credentials = useQuery({ queryKey: ["portal", "credentials", base], queryFn: () => portalClient.get<Credential[]>(`${base}/credentials`), enabled: Boolean(base) });

  const failure = usage.error ?? account.error ?? requests.error;
  if (failure) return <StatePanel kind="error" title={locale === "zh-CN" ? "概览暂时不可用" : "Overview is temporarily unavailable"} description={failure.message} requestId={failure instanceof ApiError ? failure.requestId : undefined} action={<Button variant="secondary" onClick={() => { void usage.refetch(); void account.refetch(); void requests.refetch(); }}>{locale === "zh-CN" ? "重新加载" : "Reload"}</Button>} />;
  if (usage.isLoading || account.isLoading || requests.isLoading) return <StatePanel kind="loading" title={locale === "zh-CN" ? "正在汇总最近数据" : "Summarizing recent data"} />;

  const totals = usage.data?.totals;
  const available = account.data?.available_microunits ?? 0;
  const rows = requests.data?.items ?? [];
  const credList = credentials.data ?? [];
  const hasRequests = rows.length > 0;
  const hasRestrictedKey = credList.some((c) => (c.policy?.allowed_models?.length ?? 0) > 0 || (c.policy?.allowed_cidrs?.length ?? 0) > 0);

  const columns: Array<Column<RecentRequest>> = [
    { key: "request", header: locale === "zh-CN" ? "请求 ID" : "Request ID", cell: (row) => <RequestId value={row.request_id} /> },
    { key: "model", header: locale === "zh-CN" ? "模型" : "Model", cell: (row) => row.model || "—" },
    { key: "status", header: locale === "zh-CN" ? "结果" : "Result", cell: (row) => <Badge tone={toneForStatus(row.status)}>{statusLabel(row.status, locale)}</Badge> },
    { key: "tokens", header: "Tokens", align: "end", cell: (row) => formatNumber((row.input_tokens ?? 0) + (row.output_tokens ?? 0), locale) },
    { key: "time", header: locale === "zh-CN" ? "时间" : "Time", cell: (row) => formatDate(row.started_at, locale) },
  ];

  const rangeOptions = [
    { value: "7", label: locale === "zh-CN" ? "7 天" : "7d" },
    { value: "30", label: locale === "zh-CN" ? "30 天" : "30d" },
    { value: "90", label: locale === "zh-CN" ? "90 天" : "90d" },
  ];

  const workflowCards = [
    { to: "/dashboard/credentials", icon: KeyRound, title: locale === "zh-CN" ? "API 密钥" : "API keys", description: locale === "zh-CN" ? "创建并管理项目访问凭证" : "Create and manage project credentials" },
    { to: "/dashboard/models", icon: Boxes, title: locale === "zh-CN" ? "模型目录" : "Model catalog", description: locale === "zh-CN" ? "查看能力、协议与价格" : "Review capabilities, protocols, and pricing" },
    { to: "/dashboard/usage", icon: Activity, title: locale === "zh-CN" ? "请求与用量" : "Requests & usage", description: locale === "zh-CN" ? "用请求 ID 定位问题" : "Trace issues with a request ID" },
    { to: "/dashboard/billing", icon: CreditCard, title: locale === "zh-CN" ? "账单与额度" : "Billing & credit", description: locale === "zh-CN" ? "掌握消费与可用额度" : "Keep spend and credit visible" },
  ];

  // Every item reflects state we can actually verify (PRODUCT.md 13.9: no
  // scaffolding presented as done, no fake completion states).
  const checklistItems = [
    { done: hasRequests, label: locale === "zh-CN" ? "完成首次 API 请求" : "Complete the first API call" },
    { done: hasRestrictedKey, label: locale === "zh-CN" ? "限制密钥可用模型或来源网络" : "Restrict key models or source networks" },
  ];

  const chartSeries = bucketSeries(usage.data?.series ?? [], 28);
  const max = Math.max(...(chartSeries.map((item) => item.requests) ?? [1]), 1);
  const hasRequestSeries = chartSeries.some((item) => item.requests > 0);

  return (
    <div className="portal-page-grid portal-overview">
      <PageHeader
        eyebrow={`${organization!.display_name} / ${project!.display_name}`}
        title={locale === "zh-CN" ? "概览" : "Overview"}
        description={locale === "zh-CN" ? "项目接入、可靠性、用量和额度都集中在这里。" : "A focused view of project access, reliability, usage, and credit."}
        actions={
          <div className="portal-page-actions">
            <Segmented
              label={locale === "zh-CN" ? "时间范围" : "Time range"}
              value={String(rangeDays)}
              options={rangeOptions}
              onChange={(value) => setRangeDays(Number(value))}
            />
            <Link to="/dashboard/credentials">
              <Button>{locale === "zh-CN" ? "创建 API 密钥" : "Create API key"}</Button>
            </Link>
          </div>
        }
      />

      <div className="portal-metrics">
        <MetricCard
          tone="brand"
          label={locale === "zh-CN" ? "请求数" : "Requests"}
          value={formatNumber(totals?.requests ?? 0, locale)}
          detail={locale === "zh-CN" ? `最近 ${rangeDays} 天` : `Last ${rangeDays} days`}
        />
        <MetricCard
          label={locale === "zh-CN" ? "处理 Tokens" : "Tokens processed"}
          value={formatNumber((totals?.input_tokens ?? 0) + (totals?.output_tokens ?? 0), locale, { notation: "compact" })}
          detail={`${formatNumber(totals?.input_tokens ?? 0, locale, { notation: "compact" })} in / ${formatNumber(totals?.output_tokens ?? 0, locale, { notation: "compact" })} out`}
        />
        <MetricCard
          label={locale === "zh-CN" ? "平均耗时" : "Average duration"}
          value={`${formatNumber(totals?.average_duration_ms ?? 0, locale, { maximumFractionDigits: 0 })} ms`}
          detail={locale === "zh-CN" ? "端到端请求" : "End-to-end request"}
        />
        <MetricCard
          tone={(account.data?.debt_microunits ?? 0) > 0 ? "warning" : "brand"}
          label={locale === "zh-CN" ? "可用额度" : "Available credit"}
          value={`${formatNumber(available / 1_000_000, locale, { maximumFractionDigits: 2 })} ${currency}`}
          detail={`${formatNumber((account.data?.reserved_microunits ?? 0) / 1_000_000, locale, { maximumFractionDigits: 2 })} ${currency} ${locale === "zh-CN" ? "预留" : "reserved"}`}
        />
      </div>

      <div className="portal-split-section">
        {rows.length === 0 ? (
          <Card className="portal-section-card portal-welcome-card">
            <div className="portal-section-head">
              <div>
                <span className="portal-card-kicker">{locale === "zh-CN" ? "开始使用" : "Get started"}</span>
                <h2>{locale === "zh-CN" ? "完成首次 API 调用" : "Complete your first API call"}</h2>
                <p>{locale === "zh-CN" ? "创建 API 密钥，发起首次请求，数据将在这里显示。" : "Create an API key and make your first request — data will appear here."}</p>
              </div>
              <Link to="/dashboard/docs" className="portal-inline-link">
                {locale === "zh-CN" ? "查看调用示例" : "View request example"}<ArrowUpRight size={15} />
              </Link>
            </div>
            <ul className="portal-checklist">
              {checklistItems.map(({ done, label }, index) => (
                <li key={index} className={done ? "is-complete" : ""}>
                  <span className="portal-checklist__icon" aria-hidden="true">
                    {done ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                  </span>
                  <span>{label}</span>
                </li>
              ))}
            </ul>
            <div className="portal-checklist-footer">
              <Link to="/dashboard/credentials">
                <Button>{locale === "zh-CN" ? "创建 API 密钥" : "Create API key"}</Button>
              </Link>
            </div>
          </Card>
        ) : (
          <div className="portal-overview-split">
            <Card className="portal-section-card portal-chart-card">
              <div className="portal-section-head">
                <div>
                  <span className="portal-card-kicker">{locale === "zh-CN" ? "可观测性" : "Observability"}</span>
                  <h2>{locale === "zh-CN" ? "请求趋势" : "Request trend"}</h2>
                  <p>{locale === "zh-CN" ? `最近 ${rangeDays} 天按日统计的请求摘要。` : `Daily request summaries from the last ${rangeDays} days.`}</p>
                </div>
                <Link to="/dashboard/usage" className="portal-inline-link">
                  {locale === "zh-CN" ? "查看详情" : "View details"}<ArrowUpRight size={15} />
                </Link>
              </div>
              {hasRequestSeries
                ? (
                  <div className="portal-chart" role="group" aria-label={locale === "zh-CN" ? `最近 ${rangeDays} 天每日请求量柱状图` : `Daily requests over the last ${rangeDays} days`}>
                    {chartSeries.map((item) => (
                      <div
                        key={item.bucket}
                        className="portal-chart__bar"
                        tabIndex={0}
                        style={{ height: `${Math.max(4, item.requests / max * 100)}%` }}
                        data-label={`${new Date(item.bucket).toLocaleDateString(locale)} · ${item.requests}`}
                      />
                    ))}
                  </div>
                )
                : (
                  <div className="portal-chart-empty">
                    <span className="portal-chart-empty__mark"><Activity size={18} /></span>
                    <strong>{locale === "zh-CN" ? "还没有请求数据" : "No request data yet"}</strong>
                    <p>{locale === "zh-CN" ? "完成首次调用后，这里会显示请求趋势。" : "Request trends will appear after your first successful call."}</p>
                    <Link to="/dashboard/docs" className="portal-inline-link">
                      {locale === "zh-CN" ? "查看调用示例" : "View request example"}<ArrowUpRight size={15} />
                    </Link>
                  </div>
                )
              }
            </Card>

            <Card className="portal-section-card portal-checklist-card">
              <div className="portal-section-head">
                <div>
                  <span className="portal-card-kicker">{locale === "zh-CN" ? "上线前检查" : "Before production"}</span>
                  <h2>{locale === "zh-CN" ? "生产就绪清单" : "Production checklist"}</h2>
                  <p>{locale === "zh-CN" ? "把关键的安全和额度动作留在视线内。" : "Keep the key safety and credit actions in view."}</p>
                </div>
              </div>
              <ul className="portal-checklist">
                {checklistItems.map(({ done, label }, index) => (
                  <li key={index} className={done ? "is-complete" : ""}>
                    <span className="portal-checklist__icon" aria-hidden="true">
                      {done ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                    </span>
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
              <div className="portal-checklist-footer">
                <Link to="/dashboard/docs">
                  <Button variant="secondary">{locale === "zh-CN" ? "打开快速开始" : "Open quickstart"}</Button>
                </Link>
              </div>
            </Card>
          </div>
        )}
      </div>

      <Card className="portal-section-card portal-request-card">
        <div className="portal-section-head">
          <div>
            <span className="portal-card-kicker">{locale === "zh-CN" ? "最近活动" : "Recent activity"}</span>
            <h2>{locale === "zh-CN" ? "最近请求" : "Recent requests"}</h2>
            <p>{locale === "zh-CN" ? "只展示安全元数据，不存储提示词或响应正文。" : "Safe metadata only; prompts and response bodies are not retained."}</p>
          </div>
          <Link to="/dashboard/usage" className="portal-inline-link">
            {locale === "zh-CN" ? "全部请求" : "All requests"}<ArrowUpRight size={15} />
          </Link>
        </div>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.request_id}
          caption={locale === "zh-CN" ? "最近 API 请求" : "Recent API requests"}
          empty={
            <StatePanel
              kind="empty"
              title={locale === "zh-CN" ? "还没有请求" : "No requests yet"}
              description={locale === "zh-CN" ? "创建密钥并完成首次调用后，请求将显示在这里。" : "Create an API key and make your first call to see it here."}
              action={<Link to="/dashboard/docs"><Button variant="secondary">{locale === "zh-CN" ? "查看调用示例" : "View examples"}</Button></Link>}
            />
          }
        />
      </Card>

      <div className="portal-section-kicker">
        <span>{locale === "zh-CN" ? "快捷入口" : "Quick access"}</span>
      </div>
      <div className="portal-workflow-row">
        {workflowCards.map(({ to, icon: Icon, title, description }) => (
          <Link key={to} to={to} className="portal-workflow-link">
            <span className="portal-workflow-link__icon" aria-hidden="true"><Icon size={18} /></span>
            <span className="portal-workflow-link__body">
              <strong>{title}</strong>
              <small>{description}</small>
            </span>
            <ArrowUpRight size={15} className="portal-workflow-link__arrow" aria-hidden="true" />
          </Link>
        ))}
      </div>
    </div>
  );
}
